/**
 * Action Card Component
 *
 * Reusable card for presenting action options (swap, pickup, etc.)
 * with clear visual hierarchy and accessibility features.
 */

import { cn } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircleIcon } from 'lucide-react';

export interface ActionOption {
  id: string;
  icon: string;
  title: string;
  description: string;
  popular?: boolean;
  estimatedTime?: string;
  badge?: {
    text: string;
    variant?: 'default' | 'secondary' | 'success' | 'warning';
  };
}

interface ActionCardProps {
  option: ActionOption;
  selected: boolean;
  onSelect: () => void;
  disabled?: boolean;
  className?: string;
}

export function ActionCard({
  option,
  selected,
  onSelect,
  disabled = false,
  className,
}: ActionCardProps) {
  return (
    <Card
      className={cn(
        'relative cursor-pointer transition-all duration-200',
        'hover:shadow-lg hover:scale-[1.02]',
        'focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2',
        selected && 'ring-2 ring-primary shadow-lg',
        disabled && 'opacity-60 cursor-not-allowed',
        className
      )}
      onClick={() => !disabled && onSelect()}
    >
      {/* Popular badge */}
      {option.popular && !disabled && (
        <Badge
          className="absolute -top-2 -right-2 z-10"
          variant="default"
        >
          Most Common
        </Badge>
      )}

      {/* Custom badge */}
      {option.badge && (
        <Badge
          className="absolute -top-2 -right-2 z-10"
          variant={option.badge.variant || 'default'}
        >
          {option.badge.text}
        </Badge>
      )}

      <CardContent className="p-6">
        <button
          type="button"
          className="w-full min-h-[88px] text-left focus:outline-none"
          disabled={disabled}
          aria-pressed={selected}
          aria-label={`${option.title}: ${option.description}`}
        >
          <div className="flex items-start space-x-4">
            {/* Icon */}
            <div className="flex-shrink-0 text-4xl" role="img" aria-hidden="true">
              {option.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  {option.title}
                </h3>
                {selected && (
                  <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                )}
              </div>

              <p className="text-sm text-gray-600 mb-3">
                {option.description}
              </p>

              {/* Estimated time */}
              {option.estimatedTime && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{option.estimatedTime}</span>
                </div>
              )}
            </div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
}
